import React, { useEffect, useState } from 'react'
import { FiFilter } from 'react-icons/fi'
import { salonAPI } from '../services/api'
import ServiceCard from '../components/ServiceCard'
import LoadingSpinner from '../components/LoadingSpinner'
import { useStudio } from '../context/StudioContext'

export default function Services() {
  const { studio } = useStudio()
  const [services, setServices] = useState([])
  const [filteredServices, setFilteredServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('All')

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await salonAPI.getServices()
        setServices(response.data)
        setFilteredServices(response.data)
      } catch (error) {
        console.error('Error fetching services:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchServices()
  }, [])

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredServices(services)
    } else {
      setFilteredServices(services.filter((service) => service.category === selectedCategory))
    }
  }, [selectedCategory, services])

  const categories = ['All', ...new Set(services.map((service) => service.category))]

  return (
    <div className="pb-20">
      <section className="section-shell page-section">
        <div className="surface-card px-6 py-10 sm:px-10">
          <p className="eyebrow">Services</p>
          <h1 className="mt-4 font-heading text-5xl text-stone-900 sm:text-6xl">Nails, beauty, and laser treatments shaped for the studio.</h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-stone-600 sm:text-base">
            The service lineup reflects the public brand story on Instagram: nail care, beauty detail, Triton Laser treatments, bilingual guest care, and a constant emphasis on quality, safety, and results.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {(studio?.bioLines || []).slice(0, 4).map((line) => (
              <span key={line} className="rounded-full bg-stone-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-stone-600">
                {line}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell">
        <div className="surface-card p-6 sm:p-8">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-stone-600">
            <FiFilter />
            Filter by category
          </h2>
          <div className="mt-5 flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'filter-pill-active' : 'filter-pill'}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell page-section">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid gap-6 xl:grid-cols-2">
            {filteredServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        )}

        {!loading && filteredServices.length === 0 && (
          <div className="surface-card mt-6 p-10 text-center">
            <p className="font-heading text-4xl text-stone-900">No services in this category yet.</p>
            <p className="mt-3 text-sm text-stone-600">Switch filters or check back as the studio offering evolves.</p>
          </div>
        )}
      </section>

      <section className="section-shell">
        <div className="surface-card grid gap-6 px-6 py-8 sm:px-10 lg:grid-cols-3">
          {(studio?.promises || []).map((promise) => (
            <div key={promise.title}>
              <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Studio promise</p>
              <h3 className="mt-3 font-heading text-3xl text-stone-900">{promise.title}</h3>
              <p className="mt-3 text-sm leading-7 text-stone-600">{promise.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
